import { completeInvoiceAmounts, editInvoiceAmounts } from '../workflowInvoiceAmounts';
// Reusable typed invoice workflow editor (purchase + sales).
//
// Fully controlled: the parent owns `value`, this component never overwrites
// user edits when extraction runs or goes stale — extraction state is display
// only, and applying an extraction result stays an explicit parent decision.
// All persistence, candidate queries, file uploads and network operations
// arrive as domain callbacks; this file makes no API calls and registers no
// routes.
//
// Identity rule: the operator picks a source order line from business-labelled
// candidates (order number + product + remaining quantity) supplied by the
// parent domain query. IDs are values only, never typed input. Picking a candidate confirms the mapping (and prefills an empty quantity with the source line's uninvoiced remainder, WP11); clearing it removes the mapping and refreshes the
// canonical geometry, so stale dimensions can never linger on the line.
//
// Quantity rule: the physical side reuses WarehouseQuantityField (the shared
// CommercialQuantityEditor surface + warehouseQuantityConversion), so the
// entered amount always persists its derivable pcs/m2/m3 set from the
// candidate's canonical dimensions. The commercial pair stays an independent
// basis edited through the same shared surface. Missing money/rate/quantity
// stays null (unknown), never zero, a guessed FX rate or a default unit.
import InvoiceField from './InvoiceField';
import SourceLinePicker from './SourceLinePicker';
import { useInvoiceIssuesVisible } from './invoiceIssueVisibility';
import InvoiceReviewHeader from './InvoiceReviewHeader';
import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  InputNumber,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ScanOutlined, UploadOutlined, CheckOutlined, ExclamationOutlined, InfoOutlined } from '@ant-design/icons';
import { ObjectCardTile } from '../../../components/layout/ObjectCard';
import { useTranslation } from 'react-i18next';
import { createQuantitySet } from '../../../utils/commercialQuantity';
import { formatAmountGrouped, formatCurrency, getCurrencySymbol } from '../../../utils/currency';
import { formatUnit } from '../../../utils/units';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { exceedsRemainder, fractionalPieces, hasInvoicePhysicalQuantitySet, invoiceQuantityPatch, printRoundingNote, remainderPrefill, restrictQuantitySetToSource, roundToWholePieces, snapToWholePieces, sourceQuantitySetFromLine, statedPieceFields, undoPieceRounding } from '../workflowInvoiceQuantity';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import { UNIT_PRICE_INPUT_PROPS } from '../../../utils/money';
import { PZ_QUANTITY_MAX } from '../../../utils/warehouseQuantity';
import WarehouseQuantityField from '../../warehouse/WarehouseQuantityField';
import {
  WAREHOUSE_CONVERTED_QUANTITY_MAX,
  type WarehouseQuantityPatch,
} from '../../warehouse/warehouseQuantityConversion';
import {
  isAmountInvoice,
  isApprovedSnapshot,
  isEditableWorkflowStatus,
  isStockGeneratingKind,
  type WorkflowExtractionStatus,
  type WorkflowInvoiceDraft,
  type WorkflowInvoiceLine,
  type WorkflowInvoiceSourceLineCandidate,
} from '../../../types/workflowInvoice';
import {
  lineTotalsConsistent,
  validateWorkflowInvoiceForApproval,
} from '../workflowInvoiceValidation';
import { recognitionMonthLabel } from '../invoiceRecognitionMonth';
import './WorkflowInvoiceEditor.css';

export interface WorkflowInvoiceEditorProps {
  value: WorkflowInvoiceDraft;
  onChange: (next: WorkflowInvoiceDraft) => void;
  /** Display-only extraction state; never auto-applies to `value`. */
  extractionStatus: WorkflowExtractionStatus;
  extractionError?: string | null;
  extractionModel?: string | null;
  extractionStale?: boolean;
  extractionPending?: boolean;
  /** Explicit user-triggered extraction; parent wires the real backend call. */
  onRequestExtraction: () => void;
  /**
   * Selectable source order lines from the parent domain query, labelled by
   * business data (order number + product + remaining quantity).
   */
  sourceLineCandidates?: WorkflowInvoiceSourceLineCandidate[];
  sourceLinesLoading?: boolean;
  /** Callback-provided source file upload; no file UI renders without it. */
  onUploadSourceFile?: () => void;
  uploadInFlight?: boolean;
  readOnly?: boolean;
  /** Partial invoice covers only part of the order — never full fulfillment. */
  partialCoverage?: boolean;
  /** Source order already frozen by an earlier receipt. */
  frozenOrder?: boolean;
  compactReview?: boolean;
}


let lineSequence = 0;
function nextLineKey(): string {
  lineSequence += 1;
  return `line-${Date.now()}-${lineSequence}`;
}

export function createEmptyWorkflowLine(): WorkflowInvoiceLine {
  return {
    key: nextLineKey(),
    productVariantId: null,
    productName: null,
    sourceOrderLineId: null,
    matchConfirmed: false,
    unit: null,
    quantity: null,
    quantityPcs: null,
    quantityM2: null,
    quantityM3: null,
    conversionUnit: null,
    conversionQuantity: null,
    width: null,
    height: null,
    length: null,
    thickness: null,
    commercialQuantity: null,
    commercialUnit: null,
    unitPrice: null,
    taxRate: null,
    netAmount: null,
    vatAmount: null,
    grossAmount: null,
    currency: null,
  };
}

function fromTextInput(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

const WorkflowInvoiceEditor: React.FC<WorkflowInvoiceEditorProps> = ({
  value,
  onChange,
  extractionStatus,
  extractionError,
  extractionStale,
  extractionPending,
  onRequestExtraction,
  sourceLineCandidates = [],
  sourceLinesLoading = false,
  onUploadSourceFile,
  uploadInFlight = false,
  readOnly,
  partialCoverage,
  frozenOrder,
  compactReview = false,
}) => {
  const { t } = useTranslation(['invoices', 'common']);
  const readOnlySnapshot = !isEditableWorkflowStatus(value.status);
  const approved = readOnlySnapshot || readOnly === true;
  const issues = useMemo(() => validateWorkflowInvoiceForApproval(value), [value]);
  const issuesVisible = useInvoiceIssuesVisible();
  const currencySymbol = getCurrencySymbol(value.currency ?? 'PLN');
  /** Source lines that other invoice lines already use, with that line's position number. */
  const takenByOthers = (key: string): Record<string, number> => {
    const taken: Record<string, number> = {};
    value.lines.forEach((other, position) => {
      if (other.key !== key && other.sourceOrderLineId) taken[other.sourceOrderLineId] = position + 1;
    });
    return taken;
  };
  const matchedLines = value.lines.filter(line => line.sourceOrderLineId).length;

  const patch = (next: Partial<WorkflowInvoiceDraft>) => {
    onChange({ ...value, ...next, manuallyCorrected: true });
  };

  const patchLine = (key: string, next: Partial<WorkflowInvoiceLine>) => {
    const lines = value.lines.map(line => line.key === key ? editInvoiceAmounts(line, next, value.direction === 'PURCHASE' && value.netPurchasePrices === true) : line);
    const monetaryEdit = ['unitPrice', 'netAmount', 'vatAmount', 'grossAmount', 'taxRate'].some(field => field in next);
    const updated = { ...value, lines, manuallyCorrected: true };
    onChange(monetaryEdit ? completeInvoiceAmounts({ ...updated, totalNet: null, totalVat: null, totalGross: null }) : updated);
  };

  /**
   * Lineage change: adopt the candidate's IDs and canonical geometry, confirm
   * the explicit choice, and refresh the derivable quantity set from the
   * already-entered amount so stale dimensions never linger.
   */
  const selectSourceLine = (key: string, sourceOrderLineId: string | null) => {
    const candidate = sourceLineCandidates.find(
      (entry) => entry.sourceOrderLineId === sourceOrderLineId,
    ) ?? null;
    onChange({
      ...value,
      lines: value.lines.map((line) => {
        if (line.key !== key) return line;
        if (!candidate) {
          return {
            ...line,
            sourceOrderLineId: null,
            productVariantId: null,
            matchConfirmed: false,
            width: null,
            height: null,
            length: null,
            thickness: null,
            quantityPcs: null,
            quantityM2: null,
            quantityM3: null,
            piecesRoundedFrom: null,
          };
        }
        const geometry = {
          width: candidate.width ?? null,
          height: candidate.height ?? null,
          length: candidate.length ?? null,
          thickness: candidate.thickness ?? null,
        };
        // Pieces printed on the invoice win (decision 2026-10-30-faktura-zrodlem-prawdy)
        // on the first link; a re-link re-reads the printed amount, because the
        // pieces may have been derived from the previous line's geometry.
        const stated = line.sourceOrderLineId ? null : statedPieceFields(line, candidate.sourceQuantitySet);
        return {
          ...line,
          sourceOrderLineId: candidate.sourceOrderLineId,
          productVariantId: candidate.productVariantId ?? null,
          productName: line.productName ?? candidate.productName,
          matchConfirmed: true,
          ...geometry,
          // WP11: a line without a quantity starts from the source line's
          // uninvoiced remainder; an entered quantity is only re-expressed.
          ...(stated
            ? snapToWholePieces({
              quantity: line.quantity,
              unit: line.unit,
              ...restrictQuantitySetToSource(stated, candidate.sourceQuantitySet),
            }, candidate.sourceQuantitySet)
            : remainderPrefill(line, candidate)
            ?? snapToWholePieces({
              quantity: line.quantity,
              unit: line.unit,
              ...restrictQuantitySetToSource(refreshQuantitySet(line, geometry), candidate.sourceQuantitySet),
            }, candidate.sourceQuantitySet)),
          piecesRoundedFrom: null,
        };
      }),
      manuallyCorrected: true,
    });
  };

  /** Shared quantity surface -> persisted line fields (set + commercial pair). */
  const handleQuantityPatch = (key: string, quantityPatch: WarehouseQuantityPatch) => {
    const line = value.lines.find(item => item.key === key);
    if (!line) return;
    const source = line.sourceOrderLineId
      ? sourceLineCandidates.find(candidate => candidate.sourceOrderLineId === line.sourceOrderLineId)
      : undefined;
    patchLine(key, invoiceQuantityPatch(line, quantityPatch, source?.sourceQuantitySet));
  };

  const addLine = () => {
    onChange({
      ...value,
      lines: [...value.lines, createEmptyWorkflowLine()],
      linesLoaded: true,
      manuallyCorrected: true,
    });
  };

  const removeLine = (key: string) => {
    onChange({
      ...value,
      lines: value.lines.filter((line) => line.key !== key),
      manuallyCorrected: true,
    });
  };

  const extractionTagColor =
    extractionStatus === 'SUCCESS' ? 'success' : extractionStatus === 'FAILED' ? 'error' : 'default';

  // Server-owned original OCR review context (display-only): the printed
  // unspecified TOTAL in its immutable captured currency plus bounded
  // uncertainty notes. Never an editable financial total, never copied into
  // submitted monetary fields, never relabelled with the live form currency,
  // never an approval blocker — manual corrections stay valid alongside it.
  const extractionReview = value.extractionReview ?? null;
  const hasExtractionReview = Boolean(
    extractionReview &&
      ((extractionReview.documentTotal !== null && extractionReview.documentTotal !== undefined)
        || extractionReview.notes.length > 0),
  );
  const extractionReviewTotal =
    extractionReview?.documentTotal !== null && extractionReview?.documentTotal !== undefined
      ? formatExtractionReviewTotal(extractionReview.documentTotal, extractionReview.currency)
      : null;

  return (
    <Space direction="vertical" size={0} style={{ width: '100%' }} data-testid="workflow-invoice-editor" className="wie-root">
      <section className="wie-head" aria-label={t('invoices:workflow.sections.status')}>
        <div className="wie-tags">
          <Space wrap size={6}>
            <Tag color={value.direction === 'PURCHASE' ? 'blue' : 'purple'}>
              {t(`invoices:workflow.direction.${value.direction.toLowerCase()}`)}
            </Tag>
            {isApprovedSnapshot(value) && <Tag color="success">{t('invoices:workflow.approvedSnapshot')}</Tag>}
            {isApprovedSnapshot(value) && value.settlementPeriod && <Typography.Text>{t('invoices:workflow.page.recognitionMonth')}: {recognitionMonthLabel(value.settlementPeriod)}</Typography.Text>}
            {readOnlySnapshot && !isApprovedSnapshot(value) && (
              <Tag color="default">{t('invoices:workflow.readOnlySnapshot')}</Tag>
            )}
            {value.manuallyCorrected && !approved && (
              <Tag color="orange">{t('invoices:workflow.manuallyCorrected')}</Tag>
            )}
            {partialCoverage && <Tag color="geekblue">{t('invoices:workflow.partialCoverage')}</Tag>}
            {frozenOrder && <Tag color="red">{t('invoices:workflow.frozenOrder')}</Tag>}
          </Space>
        </div>
        <div className="wie-extraction">
          <Space wrap size={8}>
            <Tag color={extractionTagColor} data-testid="workflow-extraction-status">
              {t(`invoices:workflow.extraction.${extractionStatus.toLowerCase()}`)}
            </Tag>
            {extractionStale && !approved && (
              <Tag color="warning" data-testid="workflow-extraction-stale">
                {t('invoices:workflow.extractionStale')}
              </Tag>
            )}
            <Button
              icon={<ScanOutlined />}
              onClick={onRequestExtraction}
              loading={Boolean(extractionPending)}
              disabled={approved || Boolean(extractionPending)}
              data-testid="workflow-request-extraction"
            >
            {t('invoices:workflow.actions.requestExtraction')}
          </Button>
          </Space>
        </div>
      </section>
      <div className="wie-review-context">
          {value.direction === 'PURCHASE' && value.netPurchasePrices && !readOnlySnapshot && <p className="wie-verify">{t('invoices:workflow.netPurchasePricesHint')}</p>}
          {(value.sourceFileName || onUploadSourceFile) && (
            <Space wrap data-testid="workflow-source-file">
              {value.sourceFileName && (
                <Tag icon={<UploadOutlined />} color="default">
                  {value.sourceFileName}
                </Tag>
              )}
              {onUploadSourceFile && !approved && (
                <Button
                  icon={<UploadOutlined />}
                  onClick={onUploadSourceFile}
                  loading={uploadInFlight}
                  disabled={uploadInFlight}
                  data-testid="workflow-upload-source-file"
                >
                  {t('invoices:workflow.actions.uploadSourceFile')}
                </Button>
              )}
            </Space>
          )}
          {extractionStatus === 'FAILED' && (
            <p className="wie-verify wie-verify--error">
              <strong>{t('invoices:workflow.extractionFailed')}</strong>
              {extractionError ? ` — ${extractionError}` : ''}
              {' — '}
              {t('invoices:workflow.manualFallback')}
            </p>
          )}
          {extractionStatus === 'SUCCESS' && !approved && !compactReview && (
            <p className="wie-verify" data-testid="workflow-extraction-verify">
              {t('invoices:workflow.extractionVerifyHint')}
            </p>
          )}
          {hasExtractionReview && extractionReview && (!compactReview || extractionReview.notes.length > 0) && (
            <div className="wie-verify wie-verify--review" data-testid="workflow-extraction-review">
              <span className="wie-verify-total">
                {t('invoices:workflow.extractionReviewTotalLabel')}:{' '}
                {extractionReviewTotal !== null && (
                  <span data-testid="workflow-extraction-review-total" className="wie-review-total">{extractionReviewTotal}</span>
                )}
              </span>
              <span className="wie-verify-unspecified">{t('invoices:workflow.extractionReviewUnspecified')}</span>
              {extractionReview.notes.length > 0 && (
                    <Space direction="vertical" size={4}>
                      {extractionReview.notes.map((note, index) => (
                        <Typography.Text key={`extraction-review-note-${index}`}>{note}</Typography.Text>
                      ))}
                      <Typography.Text type="secondary">
                        {t('invoices:workflow.extractionReviewHint')}
                      </Typography.Text>
                    </Space>

              )}
            </div>
          )}
          {approved && (
            <p className="wie-verify wie-verify--approved" data-testid="workflow-approved-banner">
              {t('invoices:workflow.approvedReadOnly')}
            </p>
          )}
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {value.kind !== 'UNKNOWN' && !isStockGeneratingKind(value.kind) && !(value.kind === 'CORRECTION' && value.correctsInvoiceId) && !isAmountInvoice(value) && (
                      <Alert
                        type="warning"
                        showIcon
                        message={t('invoices:workflow.nonStandardKindTitle')}
                        description={t('invoices:workflow.nonStandardKindDescription')}
                        data-testid="workflow-nonstandard-warning"
                      />
                    )}
                    {partialCoverage && (
                      <Alert
                        type="info"
                        showIcon
                        message={t('invoices:workflow.partialCoverageHint')}
                        data-testid="workflow-partial-banner"
                      />
                    )}
                    {frozenOrder && (
                      <Alert
                        type="warning"
                        showIcon
                        message={t('invoices:workflow.frozenOrderHint')}
                        data-testid="workflow-frozen-banner"
                      />
                    )}

                  </Space>
      </div>
                    {issues.length > 0 && !approved && issuesVisible && (
                      <Alert
                        type="error"
                        showIcon
                        message={t('invoices:workflow.issuesTitle')}
                        description={
                          <ul style={{ marginBottom: 0, paddingInlineStart: 18 }} data-testid="workflow-issues-list">
                            {issues.map((issue) => (
                              <li key={`${issue.field}-${issue.key}`}>{issue.field.startsWith('lines[') ? `${t('invoices:workflow.linePosition', { number: Number(issue.field.match(/\[(\d+)\]/)?.[1]) + 1 })}: ` : ''}{t(issue.key)}</li>
                            ))}
                          </ul>
                        }
                        data-testid="workflow-issues"
                      />
                    )}
      <InvoiceReviewHeader value={value} patch={patch} disabled={approved} />

      {isAmountInvoice(value) ? (
        <Alert type="info" showIcon className="wie-alert" data-testid="workflow-amount-invoice-hint"
          message={t(`invoices:workflow.amountInvoice.${value.kind === 'SETTLEMENT' ? 'settlementHint' : 'advanceHint'}`)} />
      ) : (
      <section className="wie-section" aria-label={t('invoices:workflow.linesTitle')}>
        <div className="wie-lines-head">
          <div>
            <h3 className="wie-section-title">{t('invoices:workflow.linesTitle')}</h3>
            {value.lines.length > 0 && (
              <span className="wie-lines-count">{t('invoices:workflow.lineCount', { count: value.lines.length })}
                {!approved && sourceLineCandidates.length > 0 && (
                  <span className={`wie-lines-match${matchedLines === value.lines.length ? ' wie-lines-match--done' : ''}`} data-testid="workflow-lines-matched">
                    {t('invoices:workflow.sourcePicker.matched', { matched: matchedLines, total: value.lines.length })}
                  </span>
                )}
              </span>
            )}
          </div>
          {!approved && (
            <Button icon={<PlusOutlined />} onClick={addLine} data-testid="workflow-add-line">
              {t('invoices:workflow.actions.addLine')}
            </Button>
          )}
        </div>
        {value.lines.length === 0 ? (
          <Typography.Text type="secondary" data-testid="workflow-lines-empty">
            {t('invoices:workflow.linesEmpty')}
          </Typography.Text>
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {value.lines.map((line, index) => {
              const selectedCandidate = sourceLineCandidates.find(
                (entry) => entry.sourceOrderLineId === line.sourceOrderLineId,
              ) ?? null;
              return (
              <article
                key={line.key}
                data-testid="workflow-line-card"
                className="wie-line"
                aria-label={`${t('invoices:workflow.linePosition', { number: index + 1 })}${line.productName ? ` — ${line.productName}` : ''}`}
              >
                <div className="wie-line-top">
                  <span className="wie-line-pos">{t('invoices:workflow.linePosition', { number: index + 1 })}</span>
                  {!approved && (
                    <Button
                      danger
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeLine(line.key)}
                      aria-label={t('invoices:workflow.actions.removeLine', { number: index + 1 })}
                      className="wie-line-remove"
                    />
                  )}
                </div>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Row gutter={[12, 12]}>
                    <Col xs={24}>
                      <InvoiceField issues={approved ? [] : issues} field={`lines[${index}].productName`}>
                        <Typography.Text type="secondary">{t('invoices:workflow.fields.productName')}</Typography.Text>
                        <Input
                          aria-label={t('invoices:workflow.fields.productName')}
                          value={line.productName ?? ''}
                          onChange={(event) => patchLine(line.key, { productName: fromTextInput(event.target.value) })}
                          disabled={approved}
                        />
                       </InvoiceField>
                    </Col>
                  </Row>

                  {!line.sourceOrderLineId && (line.quantityRaw != null || line.quantityUnitRaw) && <Typography.Text type="secondary">{t('invoices:workflow.fields.quantityRaw')}: {line.quantityRaw ?? '—'} · {t('invoices:workflow.fields.quantityUnitRaw')}: {line.quantityUnitRaw ? formatUnit(line.quantityUnitRaw) : '—'}</Typography.Text>}
                  <InvoiceField issues={approved ? [] : issues} field={`lines[${index}].match`}>
                    <Typography.Text type="secondary">{t('invoices:workflow.fields.sourceOrderLine')}</Typography.Text>
                    <SourceLinePicker
                      candidates={sourceLineCandidates}
                      invoiceName={line.productName}
                      quantity={sourceQuantitySetFromLine(line)}
                      takenBy={takenByOthers(line.key)}
                      value={line.sourceOrderLineId}
                      onChange={(next) => selectSourceLine(line.key, next)}
                      disabled={approved}
                      loading={sourceLinesLoading}
                      fallbackLabel={line.productName}
                      ariaLabel={t('invoices:workflow.fields.sourceOrderLine')}
                      testId={`workflow-line-source-${line.key}`}
                    />
                  </InvoiceField>

                  {selectedCandidate && !selectedCandidate.productVariantId && !approved && <Alert type="warning" showIcon
                    message={t('invoices:workflow.validation.lineCatalogRequired')}
                    action={value.direction === 'PURCHASE' && selectedCandidate.sourceOrderId
                      ? <a href={`/app/supplier-orders/${encodeURIComponent(selectedCandidate.sourceOrderId)}?mode=edit`} target="_blank" rel="noreferrer">{t('invoices:workflow.page.openSourceOrder')}</a>
                      : undefined} />}

                  <InvoiceField issues={approved ? [] : issues} fields={[`lines[${index}].quantity`, `lines[${index}].unit`, `lines[${index}].commercial`]}><WarehouseQuantityField
                    line={{
                      productVariantId: line.productVariantId,
                      quantity: line.quantity,
                      ...(hasInvoicePhysicalQuantitySet(line) ? {
                        quantityPcs: line.quantityPcs,
                        quantityM2: line.quantityM2,
                        quantityM3: line.quantityM3,
                      } : {}),
                      unit: line.unit,
                      // New input is pcs/m2/m3 only; legacy readable units
                      // stay displayable elsewhere but never enter conversion.
                      conversionUnit: line.conversionUnit !== line.unit ? toDimensionalUnit(line.conversionUnit) : null,
                      pricingQuantity: line.commercialQuantity,
                      pricingUnit: line.commercialUnit,
                      width: line.width,
                      height: line.height,
                      length: line.length,
                      thickness: line.thickness,
                    }}
                    pricingQuantity={line.commercialQuantity}
                    pricingUnit={line.commercialUnit}
                    onChange={(quantityPatch) => handleQuantityPatch(line.key, quantityPatch)}
                    maxQuantity={PZ_QUANTITY_MAX}
                    maxConvertedQuantity={WAREHOUSE_CONVERTED_QUANTITY_MAX}
                    disabled={approved}
                    emptyQuantityLabel={t('invoices:workflow.quantityRequired')}
                    noDimensionsLabel={t('invoices:workflow.conversionNoDimensions')}
                    unitLabel={t('invoices:workflow.fields.physicalUnit')}
                    printedAmount
                  />

                  </InvoiceField>
                  <WholePiecesNotice
                    line={line}
                    sourceSet={selectedCandidate?.sourceQuantitySet ?? null}
                    disabled={approved}
                    onPatch={(next) => patchLine(line.key, next)}
                  />
                  {!approved && exceedsRemainder(line, selectedCandidate) && (
                    <Typography.Text type="warning" data-testid={`workflow-line-over-remainder-${line.key}`}>
                      {t('invoices:workflow.coverage.overRemainder', {
                        remaining: `${selectedCandidate!.remainingQuantity} ${formatUnit(selectedCandidate!.remainingUnit)}`,
                      })}
                    </Typography.Text>
                  )}
                  <div className="wie-calc">
                    <div className="wie-calc__group wie-calc__group--price">
                      <span className="wie-calc__title">{t('invoices:workflow.review.priceGroup')}</span>
                      <div className="wie-calc__fields">
                        <InvoiceField issues={approved ? [] : issues} field={`lines[${index}].unitPrice`}>
                          <Typography.Text type="secondary">{t('invoices:workflow.fields.unitPrice')}</Typography.Text>
                          <InputNumber
                            aria-label={t('invoices:workflow.fields.unitPrice')}
                            value={line.unitPrice ?? null}
                            onChange={(next) => patchLine(line.key, { unitPrice: typeof next === 'number' ? next : null })}
                            disabled={approved}
                            placeholder={t('invoices:workflow.placeholders.unknown')}
                            addonAfter={`${currencySymbol}${line.commercialUnit ? ` / ${formatUnit(line.commercialUnit)}` : ''}`}
                            controls={false}
                            {...UNIT_PRICE_INPUT_PROPS}
                            {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                          />
                        </InvoiceField>
                        <InvoiceField issues={approved ? [] : issues} field={`lines[${index}].taxRate`}>
                          <Typography.Text type="secondary">{t('invoices:workflow.fields.taxRate')}</Typography.Text>
                          <InputNumber
                            aria-label={t('invoices:workflow.fields.taxRate')}
                            value={line.taxRate ?? null}
                            onChange={(next) => patchLine(line.key, { taxRate: typeof next === 'number' ? next : null })}
                            disabled={approved}
                            placeholder={t('invoices:workflow.placeholders.unknown')}
                            addonAfter="%"
                            controls={false}
                            {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                          />
                        </InvoiceField>
                      </div>
                    </div>
                    <div className="wie-calc__group wie-calc__group--amounts">
                      <span className="wie-calc__title">{t('invoices:workflow.review.amountsGroup')}</span>
                      <div className="wie-calc__fields">
                        {([['netAmount', ['netAmount', 'amounts']], ['vatAmount', ['vatAmount', 'amounts']], ['grossAmount', ['grossAmount', 'amounts']]] as const).map(([key, fieldKeys]) => (
                          <InvoiceField key={key} issues={approved ? [] : issues} fields={fieldKeys.map(name => `lines[${index}].${name}`)}>
                            <Typography.Text type="secondary">{t(`invoices:workflow.fields.${key}`)}</Typography.Text>
                            <InputNumber
                              aria-label={t(`invoices:workflow.fields.${key}`)}
                              value={line[key] ?? null}
                              onChange={(next) => patchLine(line.key, { [key]: typeof next === 'number' ? next : null })}
                              disabled={approved}
                              placeholder={t('invoices:workflow.placeholders.unknown')}
                              addonAfter={currencySymbol}
                              controls={false}
                              precision={2}
                              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                            />
                          </InvoiceField>
                        ))}
                      </div>
                    </div>
                  </div>

                  {(!(value.direction === 'PURCHASE' && value.netPurchasePrices) || readOnlySnapshot) && (line.discountAmount != null || line.discountPercent != null) && (
                  <Row gutter={[12, 12]}>
                    {(['discountPercent', 'discountAmount'] as const).map(field => <Col xs={12} key={field}>
                      <label><Typography.Text type="secondary">{t(`invoices:workflow.fields.${field}`)}</Typography.Text>
                      <InputNumber aria-label={t(`invoices:workflow.fields.${field}`)} value={line[field] ?? null} disabled={approved} onChange={next => patchLine(line.key, { [field]: typeof next === 'number' ? next : null })} style={{ width: '100%' }} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} /></label>
                    </Col>)}
                  </Row>
                  )}
                  {!lineTotalsConsistent(line) && (
                    <Alert
                      type="warning"
                      showIcon
                      className="wie-alert"
                      message={t('invoices:workflow.validation.lineAmountsInconsistent')}
                      data-testid={`workflow-line-amounts-warning-${line.key}`}
                    />
                  )}
                </Space>
              </article>
              );
            })}
          </Space>
        )}
      </section>
      )}
      <InvoiceReviewHeader details value={value} patch={patch} disabled={approved} />
    </Space>
  );
};

/**
 * Printed TOTAL presentation: the shared money formatter with the immutable
 * captured OCR currency. Unknown currency stays unlabeled (grouped amount
 * only) — the live editable form currency is never a fallback.
 */
function formatExtractionReviewTotal(total: number, currency: string | null | undefined): string {
  if (!currency) {
    return formatAmountGrouped(total);
  }
  return formatCurrency(total, currency);
}

/**
 * Whole pieces (owner 2026-10-22, workflowInvoiceQuantity): explains a printed
 * m² that is only the invoice's rounding, offers rounding a real fractional
 * piece (confirmed by the operator) and shows/undoes a confirmed rounding.
 */
const WholePiecesNotice: React.FC<{
  line: WorkflowInvoiceLine;
  sourceSet: WorkflowInvoiceSourceLineCandidate['sourceQuantitySet'] | null;
  disabled: boolean;
  onPatch: (next: Partial<WorkflowInvoiceLine>) => void;
}> = ({ line, sourceSet, disabled, onPatch }) => {
  const { t } = useTranslation(['invoices']);
  const pcs = (value: number) => `${formatLocalizedQuantity(value, 'pl')} ${formatUnit('pcs')}`;
  const amount = (value: number, unit: string) => `${formatLocalizedQuantity(value, 'pl')} ${formatUnit(unit)}`;
  const printed = amount(line.commercialQuantity ?? 0, line.commercialUnit ?? '');
  // One row: tile, one-line text (full text in `title`), the action right.
  const row = (tone: 'blue' | 'orange' | 'green', icon: React.ReactNode, testId: string, text: string,
    hint: string, action?: React.ReactNode) => (
    <div className={`wie-pieces wie-pieces--${tone}`} data-testid={`${testId}-${line.key}`}>
      <ObjectCardTile tone={tone} size="sm">{icon}</ObjectCardTile>
      <span className="wie-pieces__text" title={`${text} ${hint}`}>{text}</span>
      <Tooltip title={hint}>
        <span className="wie-pieces__why" tabIndex={0}>{t('invoices:workflow.wholePieces.why')}</span>
      </Tooltip>
      {action}
    </div>
  );
  if (line.piecesRoundedFrom != null) {
    return row('green', <CheckOutlined />, 'workflow-line-pieces-rounded',
      t('invoices:workflow.wholePieces.rounded', { from: pcs(line.piecesRoundedFrom), to: pcs(line.quantityPcs ?? 0) }),
      t('invoices:workflow.wholePieces.roundedHint', { printed }),
      !disabled && sourceSet
        ? <Button size="small" onClick={() => onPatch(undoPieceRounding(line, sourceSet))}>{t('invoices:workflow.wholePieces.undo')}</Button>
        : undefined);
  }
  const fractional = disabled ? null : fractionalPieces(line, sourceSet);
  if (fractional && sourceSet) {
    return row('orange', <ExclamationOutlined />, 'workflow-line-pieces-fractional',
      t('invoices:workflow.wholePieces.fractional', { pieces: pcs(fractional.exact) }),
      t('invoices:workflow.wholePieces.fractionalHint', { printed }),
      <Button size="small" type="primary" onClick={() => onPatch(roundToWholePieces(line, sourceSet, fractional))}
        data-testid={`workflow-line-pieces-round-${line.key}`}>
        {t('invoices:workflow.wholePieces.roundTo', { pieces: pcs(fractional.whole) })}
      </Button>);
  }
  const note = printRoundingNote(line);
  if (!note) return null;
  return row('blue', <InfoOutlined />, note.stated ? 'workflow-line-stated-pieces' : 'workflow-line-print-rounding',
    t(note.stated ? 'invoices:workflow.wholePieces.statedPieces' : 'invoices:workflow.wholePieces.printRounding', {
      pieces: pcs(note.pieces), physical: amount(note.physical, note.unit), printed: amount(note.printed, note.unit),
    }),
    t(note.stated ? 'invoices:workflow.wholePieces.statedPiecesHint' : 'invoices:workflow.wholePieces.printRoundingHint'));
};

/** Narrow a possibly-legacy unit to the dimensional conversion scope. */
function toDimensionalUnit(unit: WorkflowInvoiceLine['unit']): 'pcs' | 'm2' | 'm3' | null {
  return unit === 'pcs' || unit === 'm2' || unit === 'm3' ? unit : null;
}

/**
 * Refresh the derivable pcs/m2/m3 set for the already-entered amount after a
 * lineage change. Underivable siblings stay null (unknown), never relabelled.
 */function refreshQuantitySet(
  line: WorkflowInvoiceLine,
  geometry: { width: number | null; height: number | null; length: number | null; thickness: number | null },
): Pick<WorkflowInvoiceLine, 'quantityPcs' | 'quantityM2' | 'quantityM3'> {
  const empty = { quantityPcs: null, quantityM2: null, quantityM3: null };
  if (line.quantity === null || line.quantity === undefined || line.unit === null || !line.unit) {
    return empty;
  }
  if (line.unit !== 'pcs' && line.unit !== 'm2' && line.unit !== 'm3') {
    return empty;
  }
  const set = createQuantitySet(line.quantity, line.unit, geometry);
  return { quantityPcs: set.pcs, quantityM2: set.m2, quantityM3: set.m3 };
}

export default WorkflowInvoiceEditor;
